<template>
  <!-- SHARED BY BOTH READINGS — by-engineer and by-tenant render through this
       ONE component so "do not invent a different layout for it" is true by
       construction rather than by two hand-kept-in-sync copies. Rows AND
       their order come from deadlines.totals[breakdownKey], ALREADY sorted
       heaviest-first / name / id server-side (_rendered_breakdowns) — never
       re-derived here, same rule as not re-summing `totals` itself. The
       null-id row (Unowned / Platform) is not filtered out. -->
  <section class="bg-white border border-gray-200 rounded-lg">
    <div class="px-5 py-3 border-b border-gray-200">
      <div class="text-sm font-semibold text-gray-900">{{ title }}</div>
      <div class="text-xs text-gray-600 mt-0.5">{{ subtitle }}</div>
    </div>
    <div class="overflow-auto" style="max-height: 320px">
      <table class="w-full text-[12px]" style="border-collapse: collapse; table-layout: fixed">
        <colgroup>
          <col :style="{ width: `${rowColWidth}%` }" />
          <col v-for="col in columns" :key="col.key" :style="{ width: `${dataColWidth}%` }" />
          <col :style="{ width: `${dataColWidth}%` }" />
        </colgroup>
        <thead>
          <tr class="bg-gray-50 border-b border-gray-200 sticky top-0">
            <th class="pl-3 pr-2 py-2 text-left text-[11px] font-semibold tracking-wide uppercase text-gray-600">
              {{ rowHeader }}
            </th>
            <th
              v-for="col in columns"
              :key="col.key"
              class="px-1 py-2 text-right text-[11px] font-semibold tracking-wide uppercase text-gray-600 truncate"
              :title="col.title"
            >{{ col.label }}</th>
            <th class="px-1 pr-4 py-2 text-right text-[11px] font-semibold tracking-wide uppercase text-gray-600">
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!rows.length">
            <td :colspan="columns.length + 2" class="px-4 py-4 text-gray-600">
              No open work in scope.
            </td>
          </tr>
          <tr v-for="row in rows" :key="rowKey(row)" class="border-b border-gray-100">
            <td
              class="pl-3 pr-2 py-1.5 align-top break-words"
              :class="row.id === null ? 'text-gray-400 italic' : 'text-gray-800'"
            >{{ row.name }}</td>
            <!-- <a> INSIDE <td> is valid content — unlike the Overall table's
                 <tr>, a matrix cell can be the anchor itself. No icon: up to
                 rows × columns of them would be clutter this dense a grid
                 has no room for — the cursor + hover:underline the cell
                 already had is enough, matching the pre-tenant owner matrix. -->
            <td
              v-for="col in columns"
              :key="col.key"
              class="px-1 py-1.5 align-top text-right"
              :class="cellCount(col, row) ? 'text-gray-900' : 'text-gray-400'"
            >
              <a
                v-if="cellHref(col, row)"
                :href="cellHref(col, row)"
                target="_blank"
                rel="noopener"
                class="hover:underline"
              >{{ cellCount(col, row) }}</a>
              <template v-else>{{ cellCount(col, row) }}</template>
            </td>
            <td class="px-1 pr-4 py-1.5 align-top text-right font-semibold text-gray-900">
              <a
                v-if="totalHref(row)"
                :href="totalHref(row)"
                target="_blank"
                rel="noopener"
                class="hover:underline"
              >{{ row.count }}</a>
              <template v-else>{{ row.count }}</template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import { formatDateValue } from '../support_constants';
import {
  beyondWorkLink, overdueWorkLink, totalWorkLink, weekWorkLink,
} from './hades_deadlines';

// Row label column takes a fixed share; every bucket column (Overdue, one per
// week, Beyond, Undated) splits the rest evenly, same rule app_ui/tables asks
// for on every table here: percentage widths summing to 100 under
// table-layout: fixed.
const ROW_COL_WIDTH = 14;

export default {
  name: 'HadesBreakdownMatrix',
  props: {
    deadlines: { type: Object, required: true },
    title: { type: String, required: true },
    subtitle: { type: String, required: true },
    rowHeader: { type: String, required: true },
    // 'by_owner' | 'by_tenant' — which breakdown list every bucket carries.
    breakdownKey: { type: String, required: true },
    // The row shape's id/name fields for that breakdown — 'owner_id' /
    // 'owner_name' or 'tenant_id' / 'tenant_name'. Passed rather than
    // hard-coded so this component has exactly one copy of the table markup.
    idField: { type: String, required: true },
    nameField: { type: String, required: true },
    // The Work tab query param this breakdown links through — 'owner' or
    // 'tenant'. Support_List.parseQueryToFilters puts both in numericFields,
    // but only tenant has its own parseTenant branch that preserves
    // '__none__' — see the note on nullQueryValue below and hades_deadlines.js's
    // workLink for the full explanation.
    queryParam: { type: String, required: true },
    // Substituted for a null id when building a link. undefined (the
    // default) means a null id NEVER links. This is NOT because the owner
    // filter lacks an "unowned" concept — it has one, same as tenant's "No
    // Tenant" — but because parseQueryToFilters runs `owner` through
    // parseNum, which turns '__none__' into NaN and drops it, so a link
    // built on it would silently land on an unfiltered list. Tenant passes
    // '__none__' explicitly here because its own parseTenant branch
    // preserves the sentinel instead — Platform becomes the one linkable
    // null-id row Unowned is not. Full explanation in hades_deadlines.js's
    // workLink comment; fixing parseQueryToFilters itself is tracked in
    // kaironlibrary hades/BUGS_AND_TECH_DEBT.txt, not done here.
    nullQueryValue: { type: [String, Number], default: undefined },
  },
  computed: {
    rowColWidth() {
      return ROW_COL_WIDTH;
    },
    // +1 for the Total column, which also draws from this shared pool — the
    // colgroup has columns.length data cols PLUS Total, and app_ui/tables
    // requires the percentages to sum to exactly 100.
    dataColWidth() {
      return (100 - ROW_COL_WIDTH) / (this.columns.length + 1);
    },
    // ONE MAP PER BUCKET, BUILT HERE — once per column, not a .find() or a
    // fresh Map per cell, which would rescan every bucket rows-times for no
    // reason. A Map, not a plain object: an object stringifies its keys, so
    // Unowned's null owner_id and Platform's null tenant_id would both
    // collide with a literal "null" string key — the same shape of bug that
    // made clicking one un-investigated ticket highlight every one of them
    // (ticket.id, fixed 2026-08-31). A Map keeps `null` a real, distinct key.
    columns() {
      const idField = this.idField;
      const toMap = (bucket) => new Map(
        bucket[this.breakdownKey].map((row) => [row[idField], row.count]));
      return [
        { key: 'overdue', label: 'Overdue', title: 'Overdue',
          map: toMap(this.deadlines.overdue) },
        ...this.deadlines.weeks.map((w) => ({
          key: w.week_start, label: this.weekLabel(w.week_start),
          title: this.weekLabel(w.week_start), map: toMap(w),
        })),
        { key: 'beyond', label: 'Beyond', title: `Beyond ${this.deadlines.horizon_weeks} weeks`,
          map: toMap(this.deadlines.beyond) },
        { key: 'undated', label: 'Undated', title: 'Undated',
          map: toMap(this.deadlines.undated) },
      ];
    },
    // Rows AND their order both come from the server's own totals breakdown —
    // already heaviest-first / name / id (_rendered_breakdowns) — never
    // re-derived by summing the buckets here. Same rule as the top-level
    // totals field: a total computed twice is how the two drift.
    rows() {
      return this.deadlines.totals[this.breakdownKey].map((row) => ({
        id: row[this.idField], name: row[this.nameField], count: row.count,
      }));
    },
  },
  methods: {
    weekLabel(weekStart) {
      return formatDateValue(weekStart, { month: 'short', day: 'numeric' });
    },
    cellCount(col, row) {
      return col.map.get(row.id) || 0;
    },
    rowKey(row) {
      // A bare `row.id` collides for every null-id row if there were ever
      // more than one (there is exactly one today — Unowned or Platform —
      // but the key should not depend on that staying true).
      return row.id === null ? `${this.queryParam}:none` : row.id;
    },
    // undefined when this breakdown has no query-string-survivable sentinel
    // for a null id (owner — see nullQueryValue's own comment for why);
    // otherwise the id itself, or nullQueryValue for a null id whose
    // sentinel does survive (tenant's '__none__').
    queryValue(row) {
      return row.id !== null ? row.id : this.nullQueryValue;
    },
    // null for Undated (no due_date operator isolates it, same as the
    // top-level callout) and for any row with no query value for this
    // breakdown — never a link built on a guess.
    cellLink(col, row) {
      const value = this.queryValue(row);
      if (value === undefined) return null;
      if (col.key === 'undated') return null;
      const extra = { [this.queryParam]: value };
      if (col.key === 'overdue') return overdueWorkLink(extra);
      if (col.key === 'beyond') return beyondWorkLink(this.deadlines.weeks, extra);
      return weekWorkLink(col.key, extra);
    },
    // No date bound at all — see totalWorkLink's own comment for why that is
    // the correct query for "everything Total counts", Undated included.
    totalLink(row) {
      const value = this.queryValue(row);
      return value === undefined ? null : totalWorkLink({ [this.queryParam]: value });
    },
    // RESOLVED THROUGH $router, matching the mechanics #2325 established for
    // Hades_Detail's own deeplink — a real href a real <a> can carry, not a
    // hand-built string, so it stays correct if the route's path ever
    // changes.
    resolveHref(link) {
      return link ? this.$router.resolve(link).href : null;
    },
    cellHref(col, row) {
      return this.resolveHref(this.cellLink(col, row));
    },
    totalHref(row) {
      return this.resolveHref(this.totalLink(row));
    },
  },
};
</script>
